'use client';

import { useAppTranslation } from '@freedraw/i18n/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ArrowUp,
  Circle,
  Diamond,
  MousePointer2,
  MoveUpRight,
  Pencil,
  Sparkles,
  Square,
  StickyNote,
  Type,
  X
} from 'lucide-react';
import { useId } from 'react';

import { BRAND_NAME } from '@/components/brand-mark';
import { ArrowMarker, SvgLabel } from '@/components/landing/landing-svg';
import { BRAND, brandTint } from '@/theme';

const INK = BRAND.ink;
const SUBTLE = '#5b5b63';
const LINE = '#ece6e9';
const RAIL_TOOLS = [MousePointer2, Square, Circle, Diamond, MoveUpRight, Pencil, Type, StickyNote];

// The board the hero shows: a small plan the assistant just drew, with the app's own
// selection chrome (pink frame, "+" handle) on one shape.
function CanvasDiagram({ labels, sticky }: { labels: string[]; sticky: string }) {
  const [research, build, ready, launch, fix, yes, no] = labels;
  const arrowId = useId();
  const box = { fill: '#ffffff', stroke: INK, strokeWidth: 1.6 } as const;
  return (
    <Box
      component="svg"
      viewBox="0 0 600 300"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      sx={{ display: 'block', width: '100%', height: '100%' }}
    >
      <defs>
        <ArrowMarker id={arrowId} />
      </defs>
      <g data-editor-edge fill="none" stroke={INK} strokeWidth="1.5" markerEnd={`url(#${arrowId})`}>
        <path d="M132 72 H166" />
        <path d="M280 72 H314" />
        <path d="M432 72 H474" />
        <path d="M374 112 V242 H282" />
        <path d="M224 216 V100" />
      </g>
      <text x="453" y="62" fontSize="11" fill={SUBTLE} textAnchor="middle">{yes}</text>
      <text x="384" y="176" fontSize="11" fill={SUBTLE}>{no}</text>

      <g data-editor-node>
        <rect x="20" y="46" width="112" height="52" rx="10" {...box} fill={BRAND.pinkLight} stroke={BRAND.pinkStrong} />
        <SvgLabel x={20} y={46} width={112} height={52} size={13}>{research}</SvgLabel>
      </g>
      <g data-editor-node>
        <rect x="168" y="46" width="112" height="52" rx="10" {...box} />
        <SvgLabel x={168} y={46} width={112} height={52} size={13}>{build}</SvgLabel>
        {/* Selection chrome as the editor draws it. */}
        <rect x="162" y="40" width="124" height="64" rx="12" fill="none" stroke={BRAND.pinkStrong} strokeWidth="1.4" />
        {[[162, 40], [286, 40], [162, 104], [286, 104]].map(([cx, cy]) => (
          <rect key={`${cx}-${cy}`} x={cx! - 4} y={cy! - 4} width="8" height="8" rx="2" fill="#fff" stroke={BRAND.pinkStrong} strokeWidth="1.4" />
        ))}
        <g data-editor-plus>
          <circle cx="224" cy="18" r="9" fill={BRAND.pinkStrong} />
          <path d="M224 13.5 V22.5 M219.5 18 H228.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
        </g>
      </g>
      <g data-editor-node>
        <path d="M374 32 L432 72 L374 112 L316 72 Z" {...box} />
        <SvgLabel x={326} y={50} width={96} height={44} size={12}>{ready}</SvgLabel>
      </g>
      <g data-editor-node>
        <rect x="476" y="46" width="104" height="52" rx="10" {...box} fill={BRAND.pink} stroke={BRAND.pinkStrong} />
        <SvgLabel x={476} y={46} width={104} height={52} size={13}>{launch}</SvgLabel>
      </g>
      <g data-editor-node>
        <rect x="168" y="216" width="112" height="52" rx="10" {...box} />
        <SvgLabel x={168} y={216} width={112} height={52} size={13}>{fix}</SvgLabel>
      </g>
      <g data-editor-node transform="rotate(-4 78 222)">
        <rect x="22" y="176" width="112" height="92" rx="4" fill="#fff4c7" stroke="#e6c65c" strokeWidth="1.2" />
        <SvgLabel x={22} y={176} width={112} height={92} size={12}>{sticky}</SvgLabel>
      </g>

      {/* The assistant's cursor, finishing the last shape. */}
      <g data-editor-cursor transform="translate(520 118)">
        <path d="M0 0 L0 18 L5 13.5 L8.5 21 L11.5 19.6 L8 12.4 L14 12 Z" fill={BRAND.pinkStrong} stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
        <rect x="12" y="20" width="28" height="18" rx="9" fill={BRAND.pinkStrong} />
        <text x="26" y="32.5" fontSize="10.5" fontWeight="700" fill="#fff" textAnchor="middle">AI</text>
      </g>
    </Box>
  );
}

/** A drawn (not screenshotted) preview of the editor: tool rail, canvas and the AI panel. */
export function LandingEditorPreview() {
  const { t } = useAppTranslation();
  const labels = t('landing.editorNodes').split('|');
  return (
    <Box
      role="img"
      aria-label={t('landing.heroPreviewLabel')}
      sx={{
        borderRadius: { xs: '14px', md: '20px' },
        border: `1px solid ${LINE}`,
        bgcolor: '#ffffff',
        overflow: 'hidden',
        boxShadow: '0 30px 80px rgba(17, 17, 17, 0.12), 0 4px 14px rgba(17, 17, 17, 0.05)'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ height: 48, px: { xs: 1.5, md: 2 }, borderBottom: `1px solid ${LINE}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              borderRadius: '6px',
              bgcolor: INK,
              color: BRAND.pink,
              fontSize: 12,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {BRAND_NAME.slice(0, 1).toLowerCase()}
          </Box>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
            {t('landing.editorBoardName')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ px: 1.25, height: 30, borderRadius: '8px', border: `1px solid ${LINE}`, fontSize: 13, fontWeight: 600 }}
          >
            <Sparkles size={14} color={BRAND.pinkStrong} />
            <span>AI</span>
          </Stack>
          <Box
            sx={{
              px: 1.5,
              height: 30,
              borderRadius: '8px',
              bgcolor: BRAND.pink,
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {t('landing.editorShare')}
          </Box>
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ height: { xs: 260, sm: 360, md: 440 } }}>
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            bgcolor: '#fdfcfd',
            backgroundImage: `radial-gradient(${brandTint(0.28)} 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}
        >
          <Stack
            spacing={0.5}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              position: 'absolute',
              top: 16,
              left: 16,
              p: 0.75,
              borderRadius: '12px',
              bgcolor: '#ffffff',
              border: `1px solid ${LINE}`,
              boxShadow: '0 6px 18px rgba(17, 17, 17, 0.06)'
            }}
          >
            {RAIL_TOOLS.map((Icon, index) => (
              <Box
                key={index}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: index === 0 ? INK : SUBTLE,
                  bgcolor: index === 0 ? BRAND.pinkLight : 'transparent'
                }}
              >
                <Icon size={16} strokeWidth={1.9} />
              </Box>
            ))}
          </Stack>
          <Box sx={{ position: 'absolute', inset: { xs: '8px', sm: '24px 24px 24px 76px' } }}>
            <CanvasDiagram labels={labels} sticky={t('landing.editorSticky')} />
          </Box>
        </Box>

        <Stack
          sx={{
            display: { xs: 'none', md: 'flex' },
            width: 300,
            flexShrink: 0,
            borderLeft: `1px solid ${LINE}`,
            bgcolor: '#ffffff'
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, height: 48, borderBottom: `1px solid ${LINE}` }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Sparkles size={16} color={BRAND.pinkStrong} />
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t('landing.editorAiTitle')}</Typography>
            </Stack>
            <X size={16} color={SUBTLE} />
          </Stack>
          <Stack spacing={1.5} sx={{ flex: 1, p: 2, overflow: 'hidden' }}>
            <Box
              data-editor-message
              sx={{
                alignSelf: 'flex-end',
                maxWidth: '88%',
                px: 1.5,
                py: 1,
                borderRadius: '12px 12px 4px 12px',
                bgcolor: BRAND.pinkLight,
                fontSize: 13,
                lineHeight: 1.45
              }}
            >
              {t('landing.promptExample')}
            </Box>
            <Box
              data-editor-message
              sx={{
                alignSelf: 'flex-start',
                maxWidth: '92%',
                px: 1.5,
                py: 1,
                borderRadius: '12px 12px 12px 4px',
                bgcolor: '#f4f2f3',
                fontSize: 13,
                lineHeight: 1.45,
                color: INK
              }}
            >
              {t('landing.editorAiReply')}
            </Box>
          </Stack>
          <Box sx={{ p: 1.5, borderTop: `1px solid ${LINE}` }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ pl: 1.5, pr: 0.75, height: 40, borderRadius: '10px', border: `1px solid ${LINE}` }}
            >
              <Typography noWrap sx={{ fontSize: 13, color: '#9a9aa2' }}>
                {t('landing.editorAiInput')}
              </Typography>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: '8px',
                  bgcolor: INK,
                  color: BRAND.pink,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowUp size={15} strokeWidth={2.4} />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}
